import { useNavigate } from 'react-router'
import invariant from 'tiny-invariant'

import useAppDispatch from '@/hooks/useAppDispatch'
import { useCreatePanelWithoutAgreementsMutation } from '@/hooks/useCreatePanelWithoutAgreementsMutation'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { usePermissions } from '@/hooks/usePermissions'
import { ROUTES } from '@/lib/routes'
import {
  PANEL_INVITE_FLOW,
  proceedWithEnqueuedCompanies,
  setActiveFlow,
  setEnqueuedCompanies,
  updateCanAddPanelDirectly,
} from '@/redux/slices/panelInvite'
import { CompanyProfileItem } from '@/types/responses/companies'

interface Options {
  /**
   * Called once the relationship exists but its agreement template still needs
   * reviewing — the caller decides where to send someone for that, since it's a
   * different journey inside the profile modal than on a page.
   */
  onAgreementTemplateReviewNeeded: () => void
}

/**
 * Adding a company to the professional network, the way the company profile's
 * button does it.
 *
 * Three routes in, decided by what the paperwork needs rather than by preference:
 *
 * - Either side has a commercial agreement template, so the standard agreements
 *   form can't handle it — create the panel bare and send the user to review the
 *   template(s).
 * - The user can't manage commercial agreements, so there's nothing for them to
 *   fill in — create the panel bare and leave it there.
 * - Otherwise, hand over to the add-relationship flow, which collects the
 *   agreements as part of adding.
 *
 * Shared so the profile header and the detected-firms table can't drift apart on
 * something with compliance attached to it.
 */
export function useAddToProfessionalNetwork({
  onAgreementTemplateReviewNeeded,
}: Options) {
  const currentCompany = useCurrentCompanyOrFail()
  const permissions = usePermissions()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const createPanelWithoutAgreementsMutation =
    useCreatePanelWithoutAgreementsMutation()

  function startAddRelationshipFlow(
    directory: NonNullable<CompanyProfileItem['directory']>,
  ) {
    navigate(ROUTES.addExistingAdviser())
    dispatch(setActiveFlow(PANEL_INVITE_FLOW.addRelationship))
    dispatch(updateCanAddPanelDirectly(permissions.canAddPanelDirectly))
    dispatch(setEnqueuedCompanies([directory]))
    dispatch(proceedWithEnqueuedCompanies())
  }

  function addToProfessionalNetwork(companyProfile: CompanyProfileItem) {
    const { company, directory } = companyProfile

    invariant(directory, 'Expected directory to be set')

    const shouldReviewAgreementTemplateAfterAdd =
      !!company &&
      (currentCompany.hasAgreementTemplate || company.hasAgreementTemplate)

    if (shouldReviewAgreementTemplateAfterAdd) {
      createPanelWithoutAgreementsMutation.mutate(
        { toCompany: company['@id'] },
        { onSuccess: onAgreementTemplateReviewNeeded },
      )

      return
    }

    if (!!company && !permissions.canManageCommercialAgreements) {
      createPanelWithoutAgreementsMutation.mutate({
        toCompany: company['@id'],
      })

      return
    }

    startAddRelationshipFlow(directory)
  }

  return {
    addToProfessionalNetwork,
    isAdding: createPanelWithoutAgreementsMutation.isPending,
  }
}
